import React from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "../../data/modal";
import { showModal } from "../../data/modal";
import { RenderLabels, findObjectById } from "../Helpers.tsx";
import TabView from "../partial/TabView";
import FileList from "../partial/FileList";
import { lighten } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  Divider,
  Grid,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableRow,
  Typography,
} from "@mui/material";

import {
  Close as CloseIcon,
  Edit as EditIcon,
  TrendingFlat as TrendingFlatIcon,
} from "@mui/icons-material";
import { selectedFilteredVoyage } from "../../selectors/filterSelector.js";
import { deleteSelectedContainer, updateSelectedContainer } from "../../data/selectedcontainers.js";
import { getSelectedTerminalCalls } from "../../data/selectedterminalcalls.js";
import { normalizeId, normalizeIds } from "../utils/normalize.tsx";
import { Container } from "../../types/Container.ts";

const ContainerModal = (properties: any, show:boolean) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const container = properties.container;
  const terminals = properties.terminals;
  const containerTypes = properties.containerTypes;
  const vessels = properties.vessels;
  const terminalCalls = properties.terminalCalls;
  const labels = properties.labels;
  const startPositionStream = properties.startPositionStream;
  const parentVessel = properties.parentVessel;
  const { voyage } = useSelector(selectedFilteredVoyage);

  const getContainerVessel = (vesselId:any) => {
    return findObjectById(vessels, vesselId)
  }

  const unPositionContainer = async (container) => {
    if (!container) return;
    await dispatch(updateSelectedContainer({ ...container, position: null })).then(() => dispatch(getSelectedTerminalCalls(normalizeIds(voyage.terminalCalls))));
  };

  const toggleLoadingDate = async (c) => {
    if (!c) return;
    const hasDate = Boolean(c.loadedDate);
    const updated = {
      ...c,
      loadedDate: hasDate ? null : new Date().toISOString(),
    };
    await dispatch(updateSelectedContainer(updated)).then(() =>
      dispatch(getSelectedTerminalCalls(normalizeIds(voyage.terminalCalls)))
    );
  };

  const toggleUnloadingDate = async (c) => {
    if (!c) return;
    const hasDate = Boolean(c.unloadedDate);
    const updated = {
      ...c,
      unloadedDate: hasDate ? null : new Date().toISOString(),
    };
    await dispatch(updateSelectedContainer(updated)).then(() =>
      dispatch(getSelectedTerminalCalls(normalizeIds(voyage.terminalCalls)))
    );
  };

  const NO_CONTAINER_COLOR = '#FFFFFF';

  const unshiftContainer = async (container: Container) => {
    if (!container) return;

    if (container.shifter) {
      const unloadingTerminalCall = container.shifter.unloadingTerminalCall;

      const updatedParent = {
        ...container,
        shiftedOut: false,
        shiftedDate: null,
        shifter: null,
        unloadingTerminalCall,
      };
      
      await dispatch(updateSelectedContainer(updatedParent));
      await dispatch(deleteSelectedContainer(container.shifter["@id"]));

      startPositionStream(normalizeId(updatedParent["@id"]));
    } else if (container.shifterOriginal) {
      const unloadingTerminalCall = container.unloadingTerminalCall;

      const original = container.shifterOriginal;
      const updatedOrig = {
        ...original,
        shiftedBackIn: false,
        shiftedDate: null,
        shifterOriginal: null,
        unloadingTerminalCall,
      };

      await dispatch(updateSelectedContainer(updatedOrig));
      await dispatch(deleteSelectedContainer(container["@id"]));

      startPositionStream(normalizeId(updatedOrig["@id"]));
    }
    
    dispatch(getSelectedTerminalCalls(normalizeIds(voyage.terminalCalls)));
    dispatch(hideModal());
  };

  if (!properties.container) {
    return
  }

  return (
      <Grid container>
        <TableContainer>
          <Table aria-label="container table" className='container-modal-table'>
            <TableBody>
              { container.labels && container.labels.length > 0 ? ( 
                <TableRow>
                  <TableCell>{t("labels")}:</TableCell>
                  <TableCell>{ RenderLabels(container.labels) }</TableCell>
                </TableRow>
              ) : ''}
              <TableRow>
                  <TableCell>{t("weight")}:</TableCell>
                  <TableCell>{ container.weight && container.weight > 0 ? (container.weight/1000).toFixed(3) : 0}T</TableCell>
              </TableRow>
              <TableRow>
                  <TableCell>{t("type")}:</TableCell>
                  <TableCell>{ container.containerType && container.containerType.name ?  `${ container.containerType.abbreviation ? `${ container.containerType.abbreviation } - ` : '' }${ container.containerType.name }` : ''}</TableCell>
              </TableRow>
              <TableRow>
                  <TableCell>{t("height")}:</TableCell>
                  <TableCell>{ container.containerType?.height ?? 'default: 2.59m'}</TableCell>
              </TableRow>
              <TableRow>
                  <TableCell>{t("teu")}:</TableCell>
                  <TableCell>{ container.containerType && container.containerType.teu ?  container.containerType.teu : ''}</TableCell>
              </TableRow>
              <TableRow>
                  <TableCell>{t("loadingTerminal")}:</TableCell>
                  <TableCell>
                    { container.loadingTerminal || container.loadingTerminalCall ? (
                       <Grid className='container-planner-terminal-color' item sx={{ 
                        backgroundColor: container.loadingTerminal && container.loadingTerminalCall.terminal && container.loadingTerminalCall.terminal.color ? container.loadingTerminalCall.terminal.color : (container.loadingTerminal && container.loadingTerminal.color ? container.loadingTerminal.color : NO_CONTAINER_COLOR),
                      }}></Grid>
                    ) : ''}
                    <Grid item sx={{ fontWeight: '600' }}>
                      { container.loadingTerminalCall && container.loadingTerminalCall.terminal ? container.loadingTerminalCall.terminal.name : (container.loadingTerminal ? container.loadingTerminal.name : '')}
                    </Grid>
                    { container.loadingTerminalCall ? (
                      <>
                        &nbsp;
                        { container.loadingTerminalCall.planned ? `${t("plannedOn")} ${ dayjs(container.loadingTerminalCall.planned).format("DD/MM/YYYY HH:mm") }` : t("notPlannedYet") }
                      </>
                    ) : ''}
                    &nbsp;
                    <Button
                      size="small"
                      onClick={() => toggleLoadingDate(container)}
                      color={ container.loadedDate ? "success" : "primary"}
                      variant={ container.loadedDate ? "contained" : "text"}
                    >
                      {container.loadedDate
                        ? t("alreadyOnBoard")
                        : t("confirmLoaded")}
                    </Button>
                  </TableCell>
              </TableRow>
              <TableRow>
                  <TableCell>{t("loadingTerminalClosure")}:</TableCell>
                  <TableCell>{ container.loadingTerminalClosure ? dayjs(container.loadingTerminalClosure).format("DD/MM/YYYY HH:mm") : ""}</TableCell>
              </TableRow>
              { container.shifterOriginal && (
                <TableRow sx={{
                  backgroundColor: lighten("#1976D2", 0.8),
                }}>
                    <TableCell align="center">{t("shifted")}:</TableCell>
                    {(() => {
                      const shifter = container.shifterOriginal;
                      const terminalCall =  shifter.unloadingTerminalCall;
                      const terminal =  shifter.unloadingTerminal || terminalCall?.terminal;
    
                      return (
                        <TableCell>
                          <Box display="flex" flexWrap="nowrap" gap={1} flexDirection="column" alignItems="flex-start" minWidth="35vh">
                            <Box display="flex" flexWrap="nowrap" alignItems="center">
                              <Grid className='container-planner-terminal-color' item sx={{ 
                                backgroundColor: terminal.color ?? NO_CONTAINER_COLOR,
                              }}></Grid>&nbsp;
                              <Typography fontSize="inherit">{terminal.name}</Typography>
                              <Typography fontSize="inherit">{ shifter.shiftedDate ? `, on ${ dayjs(shifter.shiftedDate).format("DD/MM/YYYY HH:mm") }` : "" }</Typography>
                            </Box>
                            <Box display="flex" flexWrap="nowrap" alignItems="center" gap={1}>
                              { `Layer: ${ shifter.position[0] } Row: ${ shifter.position[1] } Position: ${ shifter.position[2] } `}
                              <TrendingFlatIcon size="small" color="success" />
                              { `Layer: ${ container.position[0] } Row: ${ container.position[1] } Position: ${ container.position[2] } `}
                            </Box>
                          </Box>
                          <Box>
                            <Button
                              startIcon={<CloseIcon />}
                              color="error"
                              onClick={() => unshiftContainer(container)}
                              title="Unshift container"
                              size="small"
                            >
                              {t("unshift")}
                            </Button>
                          </Box>
                        </TableCell>
                      );
                    })()}
                </TableRow>
              ) }
              { container.shifter && (
                <TableRow sx={{
                  backgroundColor: lighten("#1976D2", 0.9),
                }}>
                    <TableCell align="center">{t("shifted")}:</TableCell>
                    {(() => {
                      const shifter = container.shifter;
                      const terminalCall =  shifter.loadingTerminalCall;
                      const terminal =  shifter.loadingTerminal || terminalCall?.terminal;
    
                      return (
                        <TableCell>
                          <Box display="flex" flexWrap="nowrap" gap={1} flexDirection="column" alignItems="flex-start"  minWidth="35vh">
                            <Box display="flex" flexWrap="nowrap" alignItems="center">
                              <Grid className='container-planner-terminal-color' item sx={{ 
                                backgroundColor: terminal.color ?? NO_CONTAINER_COLOR,
                              }}></Grid>&nbsp;
                              <Typography fontSize="inherit">{terminal.name}</Typography>
                              <Typography fontSize="inherit">{ shifter.shiftedDate ? `, on ${ dayjs(shifter.shiftedDate).format("DD/MM/YYYY HH:mm") }` : "" }</Typography>
                            </Box>
                            <Box display="flex" flexWrap="nowrap" alignItems="center" gap={1}>
                              { `Layer: ${ container.position[0] } Row: ${ container.position[1] } Position: ${ container.position[2] } `}
                              <TrendingFlatIcon size="small" color="success" />
                              { `Layer: ${ shifter.position[0] } Row: ${ shifter.position[1] } Position: ${ shifter.position[2] } `}
                            </Box>
                          </Box>
                          <Box>
                            <Button
                              startIcon={<CloseIcon />}
                              color="error"
                              onClick={() => unshiftContainer(container)}
                              title="Unshift container"
                              size="small"
                            >
                              {t("unshift")}
                            </Button>
                          </Box>
                        </TableCell>
                      );
                    })()}
                </TableRow>
              ) }
              <TableRow>
                  <TableCell>{t("unloadingTerminal")}:</TableCell>
                  <TableCell>
                    { container.unloadingTerminal || container.unloadingTerminalCall ? (
                       <Grid className='container-planner-terminal-color' item sx={{ 
                        backgroundColor:  container.unloadingTerminalCall && container.unloadingTerminalCall.terminal && container.unloadingTerminalCall.terminal.color ? container.unloadingTerminalCall.terminal.color : (container.unloadingTerminal && container.unloadingTerminal.color ? container.unloadingTerminal.color : NO_CONTAINER_COLOR),
                      }}></Grid>
                    ) : ''}
                    <Grid item sx={{ fontWeight: '600' }}>
                      { container.unloadingTerminalCall && container.unloadingTerminalCall.terminal ? container.unloadingTerminalCall.terminal.name : (container.unloadingTerminal ? container.unloadingTerminal.name : '')}
                    </Grid>
                    { container.unloadingTerminalCall ? (
                      <>
                        ,&nbsp;
                        { container.unloadingTerminalCall.planned ? `Planned on ${ dayjs(container.unloadingTerminalCall.planned).format("DD/MM/YYYY HH:mm") }` : "Not yet planned." }
                      </>
                    ) : ''}
                    &nbsp;
                    <Button
                      size="small"
                      onClick={() => toggleUnloadingDate(container)}
                      color={ container.unloadedDate ? "success" : "primary"}
                      variant={ container.unloadedDate ? "contained" : "text"}
                    >
                      {container.unloadedDate
                        ? t("alreadyUnloaded")
                        : t("confirmedUnloaded")}
                    </Button>
                  </TableCell>
              </TableRow>
              <TableRow>
                  <TableCell>{t("unloadingTerminalClosure")}:</TableCell>
                  <TableCell>{ container.unloadingTerminalClosure ? dayjs(container.unloadingTerminalClosure).format("DD/MM/YYYY HH:mm"): "" }</TableCell>
              </TableRow>
              { container.position &&  container.position.length >= 3 && !container.shifter && !container.shifterOriginal ? (
                <TableRow>
                    <TableCell>{t("position")}:</TableCell>
                    <TableCell>
                      <Box display="flex" flexWrap="nowrap" gap={2} alignItems="center">
                        <Typography noWrap>
                          { `${ getContainerVessel(`/api/vessels/${container.position[3]}`).name } - Layer: ${ container.position[0] } Row: ${ container.position[1] } Position: ${ container.position[2] } `}
                        </Typography>
                        <Button
                          startIcon={<CloseIcon />}
                          color="error"
                          onClick={() => unPositionContainer(container)}
                          title="Unposition container"
                          size="small"
                        >
                          {t("unposition")}
                        </Button>
                      </Box>
                    </TableCell>
                </TableRow>
              ) : ''}
            </TableBody>
          </Table>
        </TableContainer>
        {/* <TabView views={[
          {
            label: 'Files',
            content: <FileList files={container.fileGroup && container.fileGroup.files ? container.fileGroup.files : []}/>,
          },
          {
            label: 'Notes',
            content: undefined,
          }
        ]}/>

        <Grid item xs={12} sm={12} marginTop={1} marginBottom={1}>
            <Divider orientation="horizontal" />
        </Grid> */}

        <Grid item xs={12} className='button-wrapper' sx={{ justifyContent: 'space-between' }}>
          <Button 
              onClick={() => {dispatch(hideModal())}}
          >
              <CloseIcon fontSize="inherit" />&nbsp;{t("close")}
          </Button>
          <Button 
              onClick={() => dispatch(
                showModal({
                  title: "Edit container",
                  description: "Edit container",
                  form: "container",
                  formProps: {
                    container: container,
                    terminals: terminals,
                    terminalCalls: terminalCalls,
                    vessels: vessels,
                    containerTypes: containerTypes,
                    labels: labels,
                    parentVessel: parentVessel,
                  },
                })
              )}
              variant='contained'
          >
              <EditIcon fontSize="inherit" />&nbsp;{t("edit")}
          </Button>
        </Grid>
      </Grid>
  );
}

export default ContainerModal;
